import { useState, useRef, useEffect } from 'react'
import {
  Copy,
  Share2,
  Shield,
  Clock,
  ArrowLeft,
  RefreshCw,
  Settings,
  InfoIcon,
  Fingerprint,
  Layers,
  ShieldCheck,
  KeyRound,
  Smartphone,
  Users,
  Grid,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import Sidebar from './Sidebar'
import TemporaryPassword from './TemporaryPassword'
import AccountSpecificPassword from './AccountSpecificPassword'
import FeaturesGrid from './FeaturesGrid'
import PasswordTypesGrid from './PasswordTypesGrid'
import BatchPasswordGenerator from './BatchPasswordGenerator'
import SEOHelmet from '../../../components/SEOHelmet'

// Advanced Pattern Password Generator with lines & dynamic grid
function PatternPasswordGenerator({ setPassword, gridSize = 3 }) {
  const [pattern, setPattern] = useState([])
  const [lines, setLines] = useState([])
  const containerRef = useRef(null)

  useEffect(() => {
    setPassword(pattern.map((i) => i + 1).join('-'))
  }, [pattern, setPassword])

  const handleNodeClick = (index) => {
    if (!pattern.includes(index)) {
      if (pattern.length > 0) {
        setLines([...lines, [pattern[pattern.length - 1], index]])
      }
      setPattern([...pattern, index])
    }
  }

  const resetPattern = () => {
    setPattern([])
    setLines([])
  }

  const generateRandomPattern = () => {
    let nums = Array.from({ length: gridSize * gridSize }, (_, i) => i)
    nums.sort(() => 0.5 - Math.random())
    const newPattern = nums.slice(0, Math.min(6, nums.length))
    const newLines = []
    for (let i = 1; i < newPattern.length; i++) {
      newLines.push([newPattern[i - 1], newPattern[i]])
    }
    setPattern(newPattern)
    setLines(newLines)
  }

  const getNodeCoords = (index) => {
    const node = containerRef.current.children[index]
    const rect = node.getBoundingClientRect()
    const parentRect = containerRef.current.getBoundingClientRect()
    return [
      rect.left + rect.width / 2 - parentRect.left,
      rect.top + rect.height / 2 - parentRect.top,
    ]
  }

  return (
    <div className="mb-6 p-4 border rounded-2xl bg-gray-50 shadow-inner">
      <h3 className="text-lg font-semibold mb-3 text-gray-800">
        Graphical / Pattern Password
      </h3>
      <div
        className="relative grid gap-3 justify-center mb-3"
        style={{ gridTemplateColumns: `repeat(${gridSize}, 50px)` }}
        ref={containerRef}
      >
        {Array.from({ length: gridSize * gridSize }, (_, i) => (
          <div
            key={i}
            onClick={() => handleNodeClick(i)}
            className={`w-12 h-12 border-2 rounded-full flex items-center justify-center cursor-pointer transition ${pattern.includes(i)
              ? 'bg-blue-600 border-blue-700 text-white shadow-lg'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}
          >
            {i + 1}
          </div>
        ))}

        {/* Lines between nodes */}
        <svg
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{ zIndex: 0 }}
        >
          {lines.map(([from, to], idx) => {
            const [x1, y1] = getNodeCoords(from)
            const [x2, y2] = getNodeCoords(to)
            return (
              <line
                key={idx}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#3b82f6"
                strokeWidth="3"
                strokeLinecap="round"
              />
            )
          })}
        </svg>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={generateRandomPattern}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition shadow"
        >
          Generate Random Pattern
        </button>
        <button
          onClick={resetPattern}
          className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition"
        >
          Reset
        </button>
      </div>

      {pattern.length > 0 && (
        <div className="mt-3 text-gray-700 font-mono">
          Current Pattern: {pattern.map((i) => i + 1).join('-')}
        </div>
      )}
    </div>
  )
}

// Main Advanced Password Generator Component
export default function AdvancedPasswordGenerator() {
  const [password, setPassword] = useState('')
  const [strength, setStrength] = useState('')
  const [timeToCrack, setTimeToCrack] = useState('')
  const [batchPasswords, setBatchPasswords] = useState([])
  const [numPasswords, setNumPasswords] = useState(5)
  const [context, setContext] = useState('')
  const [copied, setCopied] = useState(false)
  const [shared, setShared] = useState(false)
  const [selectedType, setSelectedType] = useState('')
  const navigate = useNavigate()
  const [link, setLink] = useState("");


  const featuresRef = useRef(null);



  const copyPassword = (pass) => {
    navigator.clipboard.writeText(pass)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const sharePassword = () => {
    if (!password) return; // ensure password exists

    const id = Math.random().toString(36).substr(2, 8); // string
    const tempLink = `${window.location.origin}/temp/${id}`;
    setLink(tempLink);

    // Copy to clipboard immediately
    navigator.clipboard.writeText(tempLink)
      .then(() => {
        setShared(true); // show "Link Copied!" feedback
        setTimeout(() => setShared(false), 2000);
      })
      .catch(() => {
        alert("Failed to copy link. Please try again.");
      });
  };


  const generatePassword = (type = 'default') => {
    let chars =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+{}[]'
    let pass = ''

    switch (type) {
      case 'otp':
        pass = String(Math.floor(100000 + Math.random() * 900000))
        break
      case 'temporary':
        for (let i = 0; i < 12; i++) {
          pass += chars[Math.floor(Math.random() * chars.length)]
        }
        break
      case 'master':
        for (let i = 0; i < 20; i++) {
          pass += chars[Math.floor(Math.random() * chars.length)]
        }
        break
      case 'account':
        for (let i = 0; i < 16; i++) {
          pass += chars[Math.floor(Math.random() * chars.length)]
        }
        if (context.toLowerCase().includes('work')) pass += 'Wrk!'
        if (context.toLowerCase().includes('bank')) pass += 'Bk#'
        break
      case 'pattern':
        setSelectedType('pattern')
        return
      case 'biometric':
        for (let i = 0; i < 18; i++) {
          pass += chars[Math.floor(Math.random() * chars.length)]
        }
        break
      default:
        for (let i = 0; i < 16; i++) {
          pass += chars[Math.floor(Math.random() * chars.length)]
        }
        break
    }

    setPassword(pass)
    setSelectedType(type)
    const strong =
      /[A-Z]/.test(pass) && /[0-9]/.test(pass) && /[^a-zA-Z0-9]/.test(pass)
    setStrength(strong ? 'Strong 🔒' : 'Medium ⚠️')
    setTimeToCrack(strong ? '5+ years' : '2-6 months')
  }

  const generateBatch = () => {
    const batch = []
    for (let i = 0; i < numPasswords; i++) {
      let pass = ''
      const chars =
        'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+{}[]'
      for (let j = 0; j < 16; j++) {
        pass += chars[Math.floor(Math.random() * chars.length)]
      }
      batch.push(pass)
    }
    setBatchPasswords(batch)
  }

  const menuItems = [
    { icon: RefreshCw, label: 'Generator', path: '/passwordgenerator' },
    { icon: Clock, label: 'Password History', path: '/passwordhistory' },
    { icon: Shield, label: 'Advanced Password', path: '/advancedpassword' },
    { icon: Settings, label: 'Settings', path: '/settings' },
    { icon: InfoIcon, label: 'About', path: '/about' },
  ]


  const features = [
    {
      icon: <ShieldCheck className="w-6 h-6 text-cyan-600" />,
      title: "Advanced Security",
      description:
        "Strong encryption and customizable password rules to protect your data.",
    },
    {
      icon: <KeyRound className="w-6 h-6 text-cyan-600" />,
      title: "Account Profiles",
      description:
        "Generate passwords tailored for different account types like banking, email, and social media.",
    },
    {
      icon: <Smartphone className="w-6 h-6 text-cyan-600" />,
      title: "Multi-Factor Authentication",
      description:
        "Add an extra layer of protection with two-factor or multi-factor authentication.",
    },
    {
      icon: <Users className="w-6 h-6 text-cyan-600" />,
      title: "Team Support",
      description:
        "Manage and share secure passwords across your team or organization.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row">
      {/* Sidebar */}
      <Sidebar menuItems={menuItems} />




      {/* Main Content */}
      <div className="flex-1 p-6 md:ml-64">
        <SEOHelmet
  title="Advanced Password Generator"
  description="Generate advanced, highly secure, and customizable passwords with symbols, numbers, and mixed cases. Perfect for professionals and high-security needs."
  keywords="advanced password generator, secure password, strong password, random password, customizable password"
  url="https://omkarmore.com/advancedpassword"
  image="https://omkarmore.com/LOGO_512.png"
/>

        {/* Top Bar */}
        <div className="sticky top-0 bg-white z-20 py-3 flex justify-center md:justify-start px-6 border-b shadow-sm mb-6">
          <button
            onClick={() => navigate("/tools")}
            className="flex items-center gap-2 text-cyan-700 font-medium hover:text-cyan-600 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Tools
          </button>
        </div>

        {/* Page Heading */}


        {/* Password Type Buttons */}
        <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-5xl mx-auto space-y-6">
          <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2 text-center md:text-left leading-tight">
            Advanced Password Generator
          </h1>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl text-center md:text-left mb-6">
            Create strong, secure passwords for all your accounts instantly. Supports one-time, temporary, master/admin, and account-specific passwords.
          </p>
          <p className="text-cyan-700 font-medium text-sm md:text-base text-center md:text-left">
            Check the options below and explore the features!
          </p>


          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { key: "default", label: "Strong Password", icon: Shield },
              { key: "otp", label: "One-Time Passcode", icon: Clock },
              { key: "temporary", label: "Temporary", icon: RefreshCw },
              { key: "master", label: "Master/Admin", icon: Settings },
              { key: "account", label: "Account-specific", icon: InfoIcon },
              { key: "pattern", label: "Pattern Lock", icon: Share2 },
              { key: "biometric", label: "Biometric-backed", icon: Fingerprint },
              { key: "batch", label: "Batch Passwords", icon: Layers },
            ].map(({ key, label, icon: Icon }) => {
              const isActive = selectedType === key;
              return (
                <button
                  key={key}
                  onClick={() => {
                    if (key === "temporary" || key === "batch" || key === "pattern") {
                      setSelectedType(key);
                    } else {
                      generatePassword(key);
                      setSelectedType(key);
                    }

                    // Scroll to features section on mobile
                    if (window.innerWidth < 768 && featuresRef.current) {
                      featuresRef.current.scrollIntoView({ behavior: "smooth" });
                    }
                  }}
                  className={`flex items-center justify-center gap-2 p-4 rounded-xl shadow-md transition font-medium text-center 
          ${isActive
                      ? "bg-gradient-to-r from-cyan-700 to-cyan-800 text-white ring-2 ring-cyan-400"
                      : "bg-gradient-to-r from-cyan-600 to-cyan-700 text-white hover:shadow-lg hover:scale-[1.02]"
                    }`}
                >
                  <Icon size={20} /> {label}
                </button>
              );
            })}
          </div>

          {/* Section Title */}
          <div ref={featuresRef} className="mt-1">

            {selectedType && (
              <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mt-6 flex items-center gap-2">
                {selectedType === "default" && <Shield size={20} className="text-cyan-600" />}
                {selectedType === "otp" && <Clock size={20} className="text-cyan-600" />}
                {selectedType === "temporary" && <RefreshCw size={20} className="text-cyan-600" />}
                {selectedType === "master" && <Settings size={20} className="text-cyan-600" />}
                {selectedType === "account" && <InfoIcon size={20} className="text-cyan-600" />}
                {selectedType === "pattern" && <Share2 size={20} className="text-cyan-600" />}
                {selectedType === "biometric" && <Fingerprint size={20} className="text-cyan-600" />}
                {selectedType === "batch" && <Layers size={20} className="text-cyan-600" />}
                {selectedType === "default" && "Strong Password Generator"}
                {selectedType === "otp" && "One-Time Passcode (OTP)"}
                {selectedType === "temporary" && "Temporary Password Generator"}
                {selectedType === "master" && "Master / Admin Password Generator"}
                {selectedType === "account" && "Account-Specific Password Generator"}
                {selectedType === "pattern" && "Pattern Lock Generator"}
                {selectedType === "biometric" && "Biometric-Backed Passwords"}
                {selectedType === "batch" && "Batch Password Generator"}
              </h2>
            )}
          </div>

          {/* Password Generation Sections */}
          {selectedType === "pattern" && (
            <PatternPasswordGenerator setPassword={setPassword} gridSize={3} />
          )}
          {selectedType === "temporary" && <TemporaryPassword />}
          {selectedType === "account" && <AccountSpecificPassword setPassword={setPassword} />}
          {selectedType === "batch" && (
            <BatchPasswordGenerator
              numPasswords={numPasswords}
              setNumPasswords={setNumPasswords}
              batchPasswords={batchPasswords}
              copyPassword={copyPassword}
              generateBatch={generateBatch}
            />
          )}


          {/* Generated Password Display */}
          {password && !["pattern", "batch"].includes(selectedType) && (
            <div className="p-4 border rounded-xl flex flex-col md:flex-row justify-between items-center bg-gray-50 shadow-inner">
              <span className="font-mono break-all text-gray-800 mb-2 md:mb-0">{password}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => copyPassword(password)}
                  className="text-gray-600 hover:text-cyan-600 transition"
                >
                  <Copy size={20} />
                </button>
                {copied && <span className="text-green-600 text-sm ml-1">Copied!</span>}
                {/* <button
              onClick={sharePassword}
              className="text-gray-600 hover:text-cyan-600 transition"
            >
              <Share2 size={20} />
            </button>
            {shared && <span className="text-blue-600 text-sm ml-1">Link Copied!</span>} */}
              </div>
            </div>
          )}

          {/* Password Strength */}
          {password && !["pattern", "batch"].includes(selectedType) && (
            <div className="flex flex-col md:flex-row justify-between text-sm text-gray-700 mt-2">
              <div className="flex items-center gap-1 mb-1 md:mb-0">
                <Shield size={16} className="text-cyan-600" />
                <span>Strength: {strength}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock size={16} className="text-cyan-600" />
                <span>Estimated Time to Crack: {timeToCrack}</span>
              </div>
            </div>
          )}

          {/* Features Section */}
          <FeaturesGrid />

          {/* Password Types Section */}
          <PasswordTypesGrid />

        </div>
      </div>
    </div>

  )
}
